<template>
  <section class="compare-section">
    <h6 class="compare-section-title">{{ group.title }}</h6>
    <div v-for="row in group.rows" :key="row.key" class="compare-row">
      <div class="compare-row-label">
        <span>{{ row.label }}</span>
        <i v-if="row.warn" class="ri-error-warning-line compare-row-warn" :title="row.warn"></i>
      </div>
      <div
        v-for="side in sides"
        :key="side"
        class="compare-row-value"
      >
        <span class="compare-row-side" :style="{ background: sideColors[side] }">{{ side.toUpperCase() }}</span>
        <span
          class="compare-row-number"
          :class="{ 'is-text': row.kind === 'text', 'is-empty': row[side] === null }"
        >{{ formatValue(row, row[side]) }}</span>
        <span v-if="row.kind !== 'text'" class="compare-row-track">
          <span class="compare-row-bar" :style="barStyle(row, side)"></span>
        </span>
      </div>
      <div class="compare-row-delta" :class="`is-${row.tone}`">{{ formatDelta(row) }}</div>
    </div>
  </section>
</template>

<script>
import { formatNum } from '@/util/number';
import { toDuration } from '@/util/date';
import { SIDE_COLORS } from '@/views/voyage/utils/voyageCompareSeries';

export default {
  name: 'CompareMetricSection',
  props: {
    group: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      sides: ['a', 'b'],
      sideColors: SIDE_COLORS,
    };
  },
  methods: {
    formatValue(row, value) {
      if (value === null || value === undefined) return 'N/A';
      if (row.kind === 'text') return value;
      if (row.kind === 'duration') return toDuration(value);
      const text = formatNum(value, row.decimals);
      if (!row.unit) return text;
      return row.unit === '%' ? `${text}%` : `${text} ${row.unit}`;
    },
    // porsi (%) pakai selisih poin, sisanya persen perubahan
    formatDelta(row) {
      const isPoints = row.deltaPoints !== null && row.deltaPoints !== undefined;
      const delta = isPoints ? row.deltaPoints : row.deltaPct;
      if (delta === null || delta === undefined) return '-';
      let sign = '';
      if (delta > 0) sign = '+';
      else if (delta < 0) sign = '−';
      return `${sign}${formatNum(Math.abs(delta), 1)}${isPoints ? ' pp' : '%'}`;
    },
    // skala per baris: nilai terbesar A/B = penuh
    barStyle(row, side) {
      const values = [row.a, row.b].filter((value) => Number.isFinite(value)).map(Math.abs);
      const max = Math.max(0, ...values);
      const value = Number.isFinite(row[side]) ? Math.abs(row[side]) : 0;
      return {
        width: max > 0 ? `${(value / max) * 100}%` : '0%',
        background: this.sideColors[side],
      };
    },
  },
};
</script>

<style scoped>
.compare-section {
  padding: 6px 0;
}

.compare-section + .compare-section {
  border-top: 1px solid var(--mz-border-soft);
}

.compare-section-title {
  margin: 4px 0;
  font-size: var(--mz-fs-sm);
  font-weight: 600;
  color: var(--mz-text-subtle);
  text-transform: uppercase;
}

.compare-row {
  display: grid;
  grid-template-columns: var(--compare-columns);
  gap: 0 12px;
  align-items: center;
  padding: 4px 6px;
  margin: 0 -6px;
  border-radius: 4px;
  font-size: var(--mz-fs-base);
}

.compare-row:hover {
  background: var(--mz-surface-2);
}

.compare-row-label {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  color: var(--mz-text);
}

.compare-row-warn {
  color: #e0a800;
  cursor: help;
}

.compare-row-value {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.compare-row-side {
  display: none;
}

/* muat "8 day 18 hr 56 min" biar awal bar segaris */
.compare-row-number {
  flex: none;
  min-width: 136px;
  text-align: right;
  font-weight: 600;
  color: var(--mz-text-emphasis);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.compare-row-number.is-text {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
}

.compare-row-number.is-empty {
  color: var(--mz-text-faint);
  font-weight: 500;
}

.compare-row-track {
  flex: 1 1 auto;
  min-width: 40px;
  height: 8px;
  border-radius: 4px;
  background: #eef2f7;
  overflow: hidden;
}

.compare-row-bar {
  display: block;
  height: 100%;
  border-radius: 4px;
}

.compare-row-delta {
  text-align: right;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.compare-row-delta.is-better {
  color: #198754;
}

.compare-row-delta.is-worse {
  color: var(--mz-notif-danger);
}

.compare-row-delta.is-neutral {
  color: var(--mz-text-subtle);
  font-weight: 500;
}

/* HP: label + delta di atas, A dan B numpuk di bawahnya */
@media (max-width: 767.98px) {
  .compare-row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 8px;
    padding: 6px;
  }

  .compare-row-delta {
    grid-column: 2;
    grid-row: 1;
  }

  .compare-row-value {
    grid-column: 1 / -1;
  }

  .compare-row-side {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    color: var(--mz-on-primary);
    font-size: var(--mz-fs-xs);
    font-weight: 600;
  }

  .compare-row-number {
    min-width: 88px;
  }
}
</style>
